<template>
  <div>
    <div class="page-header">
      <h1 class="title">Recipes <span class="title-count">{{ filteredRecipes.length }}</span></h1>
      <router-link v-if="isAdmin" to="/recipe/new" class="add-recipe-btn">+ Add Recipe</router-link>
    </div>

    <div class="toolbar">
      <div class="toolbar-search">
        <SearchBar
          id="recipe-search"
          label="Search recipes..."
          v-model="search"
        />
      </div>
      <div class="toolbar-controls">
        <select id="sort-select" v-model="sortBy" class="sort-select" aria-label="Sort recipes">
          <option value="category">Sort: Category</option>
          <option value="name">Sort: Name (A–Z)</option>
          <option value="name-desc">Sort: Name (Z–A)</option>
        </select>
        <div class="view-toggle" role="group" aria-label="View mode">
          <button
            type="button"
            :class="['view-toggle-btn', { active: viewMode === 'cards' }]"
            @click="setViewMode('cards')"
            title="Card view"
            aria-label="Card view"
          >▦</button>
          <button
            type="button"
            :class="['view-toggle-btn', { active: viewMode === 'list' }]"
            @click="setViewMode('list')"
            title="List view"
            aria-label="List view"
          >☰</button>
        </div>
      </div>
    </div>
    
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading recipes...</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="filteredRecipes.length === 0 && search" class="empty-state">
      <div class="empty-icon">🔍</div>
      <h2>No recipes found</h2>
      <p>No recipes match "{{ search }}". Try a different search term.</p>
      <button @click="search = ''" class="clear-search-btn">Clear search</button>
    </div>

    <!-- Empty state - no recipes at all -->
    <div v-else-if="recipes.length === 0 && !loading" class="empty-state">
      <div class="empty-icon">📖</div>
      <h2>No recipes yet</h2>
      <p>Your recipe collection is empty.</p>
    </div>

    <!-- Recipe listing -->
    <div v-else class="recipe-collection">
      <section
        class="category-listing"
        v-for="(category, key) in recipesByCategory"
        :key="key"
      >
        <h2 class="category-title">
          {{ key }}
          <span class="category-count">{{ category.length }}</span>
        </h2>
        <div :class="viewMode === 'cards' ? 'recipe-cards' : 'recipe-rows'">
          <router-link
            v-for="recipe in category"
            :key="recipe._id"
            :to="'/recipe/' + recipe._id"
            class="recipe-card"
          >
            <span class="recipe-card-name">{{ recipe.name }}</span>
            <span v-if="recipe.description" class="recipe-card-desc">{{ recipe.description }}</span>
          </router-link>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import SearchBar from "@/components/SearchBar.vue";

export default {
  name: "StartPage",
  components: {
    SearchBar,
  },
  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      error: false,
      errorMsg: "",
      loading: true,
      search: "",
      recipes: [],
      sortBy: "category",
      viewMode: localStorage.getItem("recipeViewMode") || "cards",
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.loading = true;
      try {
        let response = await this.$http.get("/recipe/all");
        this.recipes = response.data;
      } catch (e) {
        this.error = true;
        this.errorMsg = "Failed to load recipes";
      } finally {
        this.loading = false;
      }
    },
    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem("recipeViewMode", mode);
    },
  },
  computed: {
    filteredRecipes() {
      if (!this.search) return this.recipes;
      const searchLower = this.search.toLowerCase();
      return this.recipes.filter(recipe =>
        recipe.name.toLowerCase().includes(searchLower) ||
        recipe.category?.toLowerCase().includes(searchLower) ||
        recipe.description?.toLowerCase().includes(searchLower)
      );
    },
    sortedRecipes() {
      const recipes = [...this.filteredRecipes];
      if (this.sortBy === "name") {
        return recipes.sort((a, b) => a.name.localeCompare(b.name));
      } else if (this.sortBy === "name-desc") {
        return recipes.sort((a, b) => b.name.localeCompare(a.name));
      }
      return recipes; // Default: will be grouped by category
    },
    recipesByCategory() {
      if (this.sortBy === "name" || this.sortBy === "name-desc") {
        return { "All Recipes": this.sortedRecipes };
      }
      const grouped = _.groupBy(this.sortedRecipes, "category");
      // Sort categories alphabetically
      const sortedCategories = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
      const sorted = {};
      sortedCategories.forEach(key => {
        // Sort recipes within each category A-Z
        sorted[key] = grouped[key].sort((a, b) => a.name.localeCompare(b.name));
      });
      return sorted;
    },
  },
};
</script>

<style scoped>
@import "../styles/base.css";
@import "../styles/posts.css";

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1em;
}

.title {
  margin: 0;
}

.title-count {
  font-size: 0.5em;
  font-weight: 600;
  color: #fff;
  background: var(--accent-color);
  padding: 0.3em 0.8em;
  border-radius: 999px;
  vertical-align: middle;
}

.add-recipe-btn {
  padding: 0.6em 1.1em;
  background: var(--accent-color, #0071e3);
  color: white;
  text-decoration: none;
  border-radius: 0.6em;
  font-weight: 500;
  white-space: nowrap;
  transition: opacity 0.2s;
}

.add-recipe-btn:hover {
  opacity: 0.9;
}

/* Toolbar: search + controls */
.toolbar {
  display: flex;
  align-items: center;
  gap: 1em;
  margin: 1.25em 0 2em;
  flex-wrap: wrap;
}

.toolbar-search {
  flex: 1 1 260px;
  min-width: 0;
}

.toolbar-controls {
  display: flex;
  align-items: center;
  gap: 0.6em;
  flex-shrink: 0;
}

.sort-select {
  height: 48px;
  padding: 0 1em;
  border-radius: 0.75em;
  border: 1px solid var(--border-color, #ddd);
  background: var(--input-bg, #f2f2f2);
  color: var(--text-color, #333);
  font-size: 0.95em;
  cursor: pointer;
}

@media (max-width: 520px) {
  .toolbar-controls {
    width: 100%;
    justify-content: space-between;
  }

  .sort-select {
    flex: 1;
  }
}

.category-count {
  font-size: 0.7rem;
  font-weight: 600;
  color: #fff;
  background: var(--accent-color);
  padding: 0.15em 0.7em;
  border-radius: 999px;
  line-height: 1.7;
}

/* View toggle */
.view-toggle {
  display: inline-flex;
  height: 48px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 0.75em;
  overflow: hidden;
}

.view-toggle-btn {
  display: flex;
  align-items: center;
  padding: 0 0.9em;
  background: var(--input-bg, #f2f2f2);
  color: var(--text-color, #333);
  border: none;
  cursor: pointer;
  font-size: 1.1em;
  line-height: 1;
  transition: background 0.2s, color 0.2s;
}

.view-toggle-btn + .view-toggle-btn {
  border-left: 1px solid var(--border-color, #ddd);
}

.view-toggle-btn.active {
  background: var(--accent-color);
  color: #fff;
}

/* Category sections */
.category-listing {
  margin-bottom: 2.5em;
}

.category-title {
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 1.6em;
  margin: 0 0 0.75em;
  color: var(--text-color);
}

/* Card grid */
.recipe-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1em;
}

.recipe-card {
  display: flex;
  flex-direction: column;
  gap: 0.35em;
  padding: 1em 1.1em;
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, #ddd);
  border-radius: 0.75em;
  box-shadow: var(--shadow);
  text-decoration: none;
  color: var(--text-color);
  transition: transform 0.15s, border-color 0.2s, box-shadow 0.2s;
}

.recipe-card:hover {
  transform: translateY(-2px);
  border-color: var(--accent-color);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.recipe-card-name {
  font-weight: 600;
  font-size: 1.05em;
}

.recipe-card-desc {
  font-size: 0.85em;
  color: var(--text-muted, #666);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* List / rows */
.recipe-rows {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 0.75em;
  overflow: hidden;
}

.recipe-rows .recipe-card {
  flex-direction: row;
  align-items: baseline;
  gap: 0.75em;
  border: none;
  border-radius: 0;
  box-shadow: none;
  border-bottom: 1px solid var(--border-color, #ddd);
  padding: 0.8em 1.1em;
}

.recipe-rows .recipe-card:last-child {
  border-bottom: none;
}

.recipe-rows .recipe-card:hover {
  transform: none;
  border-color: transparent;
  border-bottom-color: var(--border-color, #ddd);
  background: var(--input-bg, #f2f2f2);
  box-shadow: none;
}

.recipe-rows .recipe-card-desc {
  -webkit-line-clamp: 1;
  line-clamp: 1;
  margin-left: auto;
  text-align: right;
  max-width: 50%;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4em 2em;
  color: var(--text-muted, #666);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color, #ddd);
  border-top-color: var(--accent-color, #0071e3);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4em 2em;
  text-align: center;
}

.empty-icon {
  font-size: 4em;
  margin-bottom: 0.5em;
}

.empty-state h2 {
  margin: 0 0 0.5em;
  font-size: 1.5em;
}

.empty-state p {
  color: var(--text-muted, #666);
  margin: 0 0 1.5em;
}

.clear-search-btn {
  padding: 0.75em 1.5em;
  background: var(--accent-color, #0071e3);
  color: white;
  border: none;
  border-radius: 0.5em;
  font-size: 1em;
  cursor: pointer;
  transition: opacity 0.2s;
}

.clear-search-btn:hover {
  opacity: 0.9;
}
</style>
